import {
  forwardRef,
  useState,
  useEffect,
  FunctionComponent,
  Ref,
  PropsWithoutRef,
  ComponentType
} from 'react';
import styled from 'styled-components';

import { BaseProps, ForwardProps, NoChildrenProp } from '../../types';

export interface IconDefinition {
  Component: ComponentType;
  viewBox?: string;
}

export interface IconModule extends IconDefinition {
  name: string;
}

export interface IconProps extends BaseProps, NoChildrenProp {
  /** The name indicating the Cosmos Icon. Reference the catalog above for the available names. */
  name: string;
  /** Ref for the wrapping element. */
  ref?: Ref<SVGSVGElement>;
}

const iconRegistry = new Map<string, IconDefinition>();

export const registerIcon = (...icons: IconModule[]) => {
  icons.forEach(({ name, ...iconDef }) => {
    if (!iconRegistry.has(name)) iconRegistry.set(name, iconDef);
  });
};

export const forceRegisterIcon = (...icons: IconModule[]) => {
  icons.forEach(({ name, ...iconDef }) => {
    iconRegistry.set(name, iconDef);
  });
};

export const StyledIcon = styled.svg`
  display: inline-block;
  fill: currentColor;
  height: 1.125rem;
  width: 1.125rem;
  vertical-align: middle;
`;

const emptyIconDefinition: Readonly<IconDefinition> = Object.freeze({ Component: () => null });

const Icon: FunctionComponent<IconProps & ForwardProps> = forwardRef(
  ({ name, ...restProps }: PropsWithoutRef<IconProps>, ref: IconProps['ref']) => {
    const [iconDef, setIconDef] = useState(iconRegistry.get(name) ?? emptyIconDefinition);

    useEffect(() => {
      if (iconRegistry.has(name)) {
        setIconDef(iconRegistry.get(name)!);
        return;
      }

      const controller = new AbortController();
      import(`./icons/${encodeURIComponent(name)}.icon`)
        .then((module: IconModule) => {
          if (!module.name || !module.Component) throw new Error('Malformed icon definition');

          registerIcon(module);
          if (!controller.signal.aborted)
            setIconDef(iconRegistry.get(module.name) ?? emptyIconDefinition);
        })
        .catch(() => {
          if (!controller.signal.aborted) setIconDef(emptyIconDefinition);
        });

      return () => {
        controller.abort();
      };
    }, [name]);

    return (
      <StyledIcon
        {...restProps}
        role='img'
        ref={ref}
        xmlns='http://www.w3.org/2000/svg'
        viewBox={iconDef.viewBox}
      >
        <iconDef.Component />
      </StyledIcon>
    );
  }
);

export default Icon;
